// src/components/AudioClipModal.js
/**
 * AudioClipModal — 音频快剪弹窗（中等面板 70vw × 400px）
 *
 * ┌────────────────────────────────────────────────────────────────┐
 * │  功能概述                                                      │
 * │  在时间轴上拖动入点(绿)/出点(红)，导出 OSS 裁剪片段，           │
 * │  生成新音频节点。时间轴背景显示真实音频波形。                     │
 * └────────────────────────────────────────────────────────────────┘
 *
 * Props:
 *   @param {string}   previewUrl     - 本地预览 blob:// URL
 *   @param {Function} onClose        - 关闭弹窗回调
 *   @param {Function} onExportAsync  - 异步导出回调 (inPoint, outPoint)，弹窗立即关闭
 *
 * 工作流（异步非阻塞）：
 *   1. 前端：加载音频 → Web Audio API 提取波形 → Canvas 绘制
 *   2. 用户拖拽入出点 或 按 I/O 键设置裁剪范围
 *   3. 点击「导出片段」→ 调用 onExportAsync(inPoint, outPoint) → 弹窗立即关闭
 *   4. AudioNodeView 负责后台 FFmpeg 裁剪 → 本地 blob 预览 → 后台上传 OSS
 *   ※ 用户无需等待裁剪完成即可继续操作
 *
 * 关键设计：
 *   ① 拖拽使用 ref（非 useState），mouseDown 时直接注册 window 事件
 *   ② 最小裁剪间隔 0.1 秒，毫秒级精确裁剪
 *   ③ 时间显示 MM:SS.cc（厘秒精度）
 *   ④ createPortal 渲染到 document.body，避免父组件 overflow 裁剪
 *   ⑤ 单击时间轴即时定位播放头（mouseDown 时立即 applyPosition）
 *
 * 快捷键：
 *   空格 = 播放/暂停 | I = 设入点 | O = 设出点 | Esc = 关闭
 */

import React, { useState, useEffect, useRef, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { Scissors, Play, Pause, X } from 'lucide-react';
import { getSharedAudioContext } from '../services/audioContextService';

// ── 颜色主题（VS Code 暗色风格，与 VideoClipModal 一致）──────────────────────

const C = {
  bg: 'var(--ed-viewport)',   // 面板主背景（深色=#1e1e1e）
  panel: 'var(--ed-panel)',   // 面板/信息栏底（深色=#252526）
  surface: 'var(--ed-header)', // 时间轴/按钮表面（深色=#2d2d30）
  border: 'var(--ed-border)', // 边框（深色=#3e3e42）
  accent: '#0078d7',          // 主色调 — 语义蓝，保持
  inColor: '#22c55e',         // 入点色（绿）— 功能色，保持
  outColor: '#ef4444',        // 出点色（红）— 功能色，保持
  playhead: '#f59e0b',        // 播放头（橙）— 功能色，保持
  selectedBg: 'rgba(0,120,215,0.15)',    // 选区高亮 — 保持
  selectedBorder: 'rgba(0,120,215,0.6)', // 选区边框 — 保持
  text: 'var(--ed-text)',     // 普通文字（深色=#cccccc）
  textBright: 'var(--text-body)', // 高亮文字（深色=#e2e8f0）
  textMuted: 'var(--ed-muted)', // 次要文字（深色=#808080）
  hoverBg: 'var(--ed-hover)', // 悬停背景（深色=#37373d）
};

/**
 * 格式化秒数为 MM:SS.cc（厘秒精度）
 */
function fmt(seconds) {
  if (!seconds || isNaN(seconds)) return '00:00.00';
  const m = Math.floor(seconds / 60);
  const s = Math.floor(seconds % 60);
  const cs = Math.floor((seconds % 1) * 100);
  return `${m.toString().padStart(2, '0')}:${s.toString().padStart(2, '0')}.${cs.toString().padStart(2, '0')}`;
}

export default function AudioClipModal({ previewUrl, onClose, onExportAsync }) {

  /* ── DOM Refs ──────────────────────────────────────────────────────── */
  const audioRef = useRef(null);
  const timelineRef = useRef(null);
  const waveCanvasRef = useRef(null);

  /* ── 音频播放状态 ──────────────────────────────────────────────────── */
  const [duration, setDuration] = useState(0);
  const [currentTime, setCurrentTime] = useState(0);
  const [isPlaying, setIsPlaying] = useState(false);

  /* ── 入出点 ────────────────────────────────────────────────────────── */
  const [inPoint, setInPoint] = useState(0);
  const [outPoint, setOutPoint] = useState(0);

  /* ── 波形数据（Float32Array，~800 采样，0-1） ───────────────────── */
  const [waveform, setWaveform] = useState(null);

  /* ── (导出已移至 AudioNodeView 异步处理，弹窗不再等待) ────────── */

  /* ── 波形容器尺寸（ResizeObserver 同步，确保弹窗渲染后波形铺满） ──── */
  const [canvasDims, setCanvasDims] = useState({ w: 0, h: 0 });

  /* ── 拖拽 Ref（与 VideoClipModal 相同的 ref-based 模式） ──────────── */
  const dragRef = useRef(null);
  const inRef = useRef(0);
  const outRef = useRef(0);
  const durRef = useRef(0);

  useEffect(() => { inRef.current = inPoint; }, [inPoint]);
  useEffect(() => { outRef.current = outPoint; }, [outPoint]);
  useEffect(() => { durRef.current = duration; }, [duration]);

  const canExport = !!previewUrl && duration > 0;

  // ── 音频事件回调 ──────────────────────────────────────────────────────

  const handleLoadedMetadata = () => {
    const dur = audioRef.current?.duration || 0;
    setDuration(dur);
    setInPoint(0);
    setOutPoint(dur);
  };

  const handleTimeUpdate = () => {
    if (!audioRef.current) return;
    const t = audioRef.current.currentTime;
    setCurrentTime(t);
    if (t >= outRef.current) {
      audioRef.current.pause();
      audioRef.current.currentTime = outRef.current;
      setIsPlaying(false);
    }
  };

  /**
   * 播放/暂停切换
   * 到达出点附近（≤0.05s）→ 自动跳回入点
   */
  const togglePlay = useCallback(() => {
    const a = audioRef.current;
    if (!a) return;
    if (a.paused || a.ended) {
      if (a.currentTime < inRef.current || a.currentTime >= outRef.current - 0.05) {
        a.currentTime = inRef.current;
      }
      a.muted = false;
      a.volume = 1.0;
      a.play().catch(() => {});
      setIsPlaying(true);
    } else {
      a.pause();
      setIsPlaying(false);
    }
  }, []);

  const seekTo = useCallback((t) => {
    if (audioRef.current) {
      audioRef.current.currentTime = Math.max(0, Math.min(durRef.current, t));
      setCurrentTime(audioRef.current.currentTime);
    }
  }, []);

  // ── 时间轴拖拽 ────────────────────────────────────────────────────────

  const getTimeFromX = useCallback((clientX) => {
    if (!timelineRef.current || durRef.current <= 0) return 0;
    const rect = timelineRef.current.getBoundingClientRect();
    const ratio = Math.max(0, Math.min(1, (clientX - rect.left) / rect.width));
    return ratio * durRef.current;
  }, []);

  const startDrag = useCallback((e, type) => {
    e.preventDefault();
    e.stopPropagation();
    dragRef.current = type;

    const MIN_GAP = 0.1;

    const applyPosition = (clientX) => {
      const t = getTimeFromX(clientX);
      if (dragRef.current === 'in') {
        const clamped = Math.min(t, outRef.current - MIN_GAP);
        const safe = Math.max(0, clamped);
        inRef.current = safe;
        setInPoint(safe);
        seekTo(safe);
      } else if (dragRef.current === 'out') {
        const clamped = Math.max(t, inRef.current + MIN_GAP);
        const safe = Math.min(durRef.current, clamped);
        outRef.current = safe;
        setOutPoint(safe);
        seekTo(safe);
      } else if (dragRef.current === 'playhead') {
        const clamped = Math.max(inRef.current, Math.min(outRef.current, t));
        seekTo(clamped);
      }
    };

    // 立即定位到点击位置（解决单击不移动的问题）
    applyPosition(e.clientX);

    const onMove = (ev) => applyPosition(ev.clientX);

    const onUp = () => {
      dragRef.current = null;
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
    };

    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
  }, [getTimeFromX, seekTo]);

  // ── 波形提取（Web Audio API） ─────────────────────────────────────────

  useEffect(() => {
    if (!previewUrl) return;
    let cancelled = false;

    (async () => {
      try {
        const resp = await fetch(previewUrl);
        if (cancelled) return;
        const arrayBuf = await resp.arrayBuffer();
        if (cancelled) return;

        // 🔊 使用全局单例 AudioContext，避免浏览器 6 个上限被耗尽
        const audioCtx = getSharedAudioContext();
        let audioBuf;
        try {
          audioBuf = await audioCtx.decodeAudioData(arrayBuf);
        } catch {
          return;
        }
        if (cancelled) return;

        const raw = audioBuf.getChannelData(0);
        // 采样条数 = 时间轴容器像素宽度 × 2（Retina 适配）
        // 每条至少 1.5px 宽以避免 sub-pixel aliasing 导致波形看起来像噪声
        const containerW = timelineRef.current?.clientWidth || 600;
        const BARS = Math.min(Math.round(containerW * 0.7), 500);
        const blockSize = Math.floor(raw.length / BARS) || 1;
        const peaks = new Float32Array(BARS);
        for (let i = 0; i < BARS; i++) {
          let sum = 0;
          const offset = i * blockSize;
          for (let j = 0; j < blockSize; j++) {
            sum += Math.abs(raw[offset + j] || 0);
          }
          peaks[i] = sum / blockSize;
        }
        const maxPeak = Math.max(...peaks) || 1;
        for (let i = 0; i < BARS; i++) peaks[i] /= maxPeak;

        if (!cancelled) setWaveform(peaks);
        // 单例 AudioContext 不关闭，供后续复用
      } catch (e) {
        console.warn('[AudioClip] 波形提取失败:', e.message);
      }
    })();

    return () => { cancelled = true; };
  }, [previewUrl]);

  // ── 监听时间轴容器尺寸（确保弹窗完成布局后再绘制波形） ──────────────

  useEffect(() => {
    const el = timelineRef.current;
    if (!el) return;
    const measure = () => {
      const { width, height } = el.getBoundingClientRect();
      const rw = Math.round(width), rh = Math.round(height);
      setCanvasDims(prev => (prev.w === rw && prev.h === rh) ? prev : { w: rw, h: rh });
    };
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  // ── 绘制波形到 Canvas ─────────────────────────────────────────────────
  // canvasDims 仅作为重绘触发器；实际尺寸从 timelineRef 容器直接读取。
  useEffect(() => {
    const canvas = waveCanvasRef.current;
    const container = timelineRef.current;
    if (!canvas || !container || !waveform) return;

    const w = container.clientWidth;
    const h = container.clientHeight;
    if (w <= 0 || h <= 0) return;

    const dpr = window.devicePixelRatio || 1;
    canvas.width = w * dpr;
    canvas.height = h * dpr;
    canvas.style.width = w + 'px';
    canvas.style.height = h + 'px';

    const ctx = canvas.getContext('2d');
    ctx.scale(dpr, dpr);
    ctx.clearRect(0, 0, w, h);

    const barCount = waveform.length;
    const barW = w / barCount;
    const midY = h / 2;

    ctx.fillStyle = 'rgba(100, 160, 255, 0.35)';
    for (let i = 0; i < barCount; i++) {
      const amp = waveform[i] * midY * 0.85;
      ctx.fillRect(i * barW, midY - amp, Math.max(barW - 0.5, 0.5), amp * 2);
    }
  }, [waveform, canvasDims]);

  // ── 快捷键 ──────────────────────────────────────────────────────────────

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === 'Escape') { onClose(); return; }
      if (e.key === ' ') { e.preventDefault(); togglePlay(); return; }
      if (e.key === 'i' || e.key === 'I') {
        const t = audioRef.current?.currentTime ?? 0;
        if (t < outRef.current - 0.1) { setInPoint(t); inRef.current = t; }
      }
      if (e.key === 'o' || e.key === 'O') {
        const t = audioRef.current?.currentTime ?? 0;
        if (t > inRef.current + 0.1) { setOutPoint(t); outRef.current = t; }
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose, togglePlay]);

  // ── 导出（异步非阻塞：只传出入出点，立即关闭弹窗） ────────────────

  const handleExport = () => {
    if (!canExport) return;
    onExportAsync?.(inPoint, outPoint);
  };

  // ── 计算百分比 ────────────────────────────────────────────────────────

  const inPct = duration > 0 ? (inPoint / duration) * 100 : 0;
  const outPct = duration > 0 ? (outPoint / duration) * 100 : 100;
  const playPct = duration > 0 ? (currentTime / duration) * 100 : 0;
  const clipDur = outPoint - inPoint;

  // ── 渲染 ──────────────────────────────────────────────────────────────

  return createPortal(
    <div style={{
      position: 'fixed', inset: 0,
      background: 'rgba(0,0,0,0.75)',
      backdropFilter: 'blur(4px)',
      zIndex: 999999,
      display: 'flex', alignItems: 'center', justifyContent: 'center',
    }}
      onClick={e => { if (e.target === e.currentTarget) onClose(); }}
    >
      {/* 主面板 */}
      <div style={{
        width: '70vw', maxWidth: '900px', minWidth: '500px',
        background: C.bg, borderRadius: '12px',
        border: `1px solid ${C.border}`,
        boxShadow: '0 24px 80px rgba(0,0,0,0.7)',
        display: 'flex', flexDirection: 'column',
        overflow: 'hidden',
      }}
        onClick={e => e.stopPropagation()}
      >
        {/* 标题栏 */}
        <div style={{
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          padding: '12px 16px',
          borderBottom: `1px solid ${C.border}`,
          background: C.panel,
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <Scissors size={16} color={C.accent} />
            <span style={{ color: C.textBright, fontSize: '14px', fontWeight: 600 }}>音频快剪</span>
            <span style={{ color: C.textMuted, fontSize: '11px', marginLeft: '12px' }}>
              Space=播放 · I=入点 · O=出点 · Esc=关闭
            </span>
          </div>
          <button onClick={onClose} style={{
            background: 'none', border: 'none', cursor: 'pointer',
            color: C.textMuted, padding: '4px',
            display: 'flex', alignItems: 'center',
          }}><X size={18} /></button>
        </div>

        {/* 隐藏的 audio 标签 */}
        <audio
          ref={audioRef}
          src={previewUrl}
          crossOrigin="anonymous"
          onLoadedMetadata={handleLoadedMetadata}
          onTimeUpdate={handleTimeUpdate}
          onEnded={() => { setIsPlaying(false); setCurrentTime(outRef.current); }}
        />

        {/* 信息栏 */}
        <div style={{
          display: 'flex', alignItems: 'center', gap: '16px',
          padding: '10px 16px',
          borderBottom: `1px solid ${C.border}`,
          background: C.panel,
          fontSize: '12px', fontFamily: 'monospace',
        }}>
          <span style={{ color: C.inColor }}>IN {fmt(inPoint)}</span>
          <span style={{ color: C.outColor }}>OUT {fmt(outPoint)}</span>
          <span style={{ color: C.textMuted }}>|</span>
          <span style={{ color: C.textBright }}>片段 {fmt(clipDur)}</span>
          <span style={{ color: C.textMuted }}>|</span>
          <span style={{ color: C.playhead }}>▶ {fmt(currentTime)}</span>
          <span style={{ color: C.textMuted }}>/ {fmt(duration)}</span>
        </div>

        {/* 时间轴 */}
        <div style={{ padding: '16px 16px 8px' }}>
          <div
            ref={timelineRef}
            className="nodrag"
            onMouseDown={(e) => startDrag(e, 'playhead')}
            style={{
              position: 'relative',
              height: '80px',
              background: C.surface,
              borderRadius: '6px',
              border: `1px solid ${C.border}`,
              cursor: 'pointer',
              overflow: 'hidden',
              userSelect: 'none',
            }}
          >
            {/* 波形 canvas */}
            <div style={{ position: 'absolute', inset: 0 }}>
              <canvas ref={waveCanvasRef} style={{ position: 'absolute', top: 0, left: 0 }} />
            </div>

            {/* 选区高亮 */}
            <div style={{
              position: 'absolute', top: 0, bottom: 0,
              left: `${inPct}%`, width: `${outPct - inPct}%`,
              background: C.selectedBg,
              borderLeft: `2px solid ${C.selectedBorder}`,
              borderRight: `2px solid ${C.selectedBorder}`,
              pointerEvents: 'none',
            }} />

            {/* 入点手柄 */}
            <div
              onMouseDown={(e) => { e.stopPropagation(); startDrag(e, 'in'); }}
              style={{
                position: 'absolute', top: 0, bottom: 0,
                left: `${inPct}%`, transform: 'translateX(-50%)',
                width: '14px', cursor: 'ew-resize', zIndex: 10,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
              }}
            >
              <div style={{
                width: '4px', height: '100%', borderRadius: '2px',
                background: C.inColor, opacity: 0.9,
              }} />
            </div>

            {/* 出点手柄 */}
            <div
              onMouseDown={(e) => { e.stopPropagation(); startDrag(e, 'out'); }}
              style={{
                position: 'absolute', top: 0, bottom: 0,
                left: `${outPct}%`, transform: 'translateX(-50%)',
                width: '14px', cursor: 'ew-resize', zIndex: 10,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
              }}
            >
              <div style={{
                width: '4px', height: '100%', borderRadius: '2px',
                background: C.outColor, opacity: 0.9,
              }} />
            </div>

            {/* 播放头 */}
            <div style={{
              position: 'absolute', top: '-2px',
              left: `${playPct}%`, transform: 'translateX(-50%)',
              pointerEvents: 'none', zIndex: 20,
              display: 'flex', flexDirection: 'column', alignItems: 'center',
            }}>
              {/* 倒三角 */}
              <div style={{
                width: 0, height: 0,
                borderLeft: '5px solid transparent',
                borderRight: '5px solid transparent',
                borderTop: `7px solid ${C.playhead}`,
              }} />
              {/* 竖线 */}
              <div style={{ width: '2px', height: '80px', background: C.playhead, opacity: 0.8 }} />
            </div>
          </div>
        </div>

        {/* 操作按钮行 */}
        <div style={{
          display: 'flex', alignItems: 'center', gap: '8px',
          padding: '8px 16px 14px',
        }}>
          {/* 入点按钮 */}
          <AToolBtn onClick={() => {
            const t = audioRef.current?.currentTime ?? 0;
            if (t < outRef.current - 0.1) { setInPoint(t); inRef.current = t; }
          }} color={C.inColor}>设入点 (I)</AToolBtn>

          {/* 出点按钮 */}
          <AToolBtn onClick={() => {
            const t = audioRef.current?.currentTime ?? 0;
            if (t > inRef.current + 0.1) { setOutPoint(t); outRef.current = t; }
          }} color={C.outColor}>设出点 (O)</AToolBtn>

          {/* 播放/暂停 */}
          <AToolBtn onClick={togglePlay} color={C.playhead}>
            {isPlaying ? <><Pause size={11} /> 暂停</> : <><Play size={11} /> 播放</>}
          </AToolBtn>

          <div style={{ flex: 1 }} />

          {/* 取消 */}
          <button onClick={onClose} style={{
            padding: '6px 14px', borderRadius: '6px',
            border: `1px solid ${C.border}`, background: 'transparent',
            color: C.textMuted, fontSize: '12px', cursor: 'pointer',
          }}>取消</button>

          {/* 导出按钮 */}
          <button
            onClick={handleExport}
            disabled={!canExport || clipDur < 0.1}
            style={{
              padding: '6px 20px', borderRadius: '6px',
              background: (!canExport || clipDur < 0.1) ? '#374151' : C.accent,
              border: 'none',
              color: (!canExport || clipDur < 0.1) ? C.textMuted : '#fff',
              fontSize: '12px', fontWeight: 600,
              cursor: (!canExport || clipDur < 0.1) ? 'not-allowed' : 'pointer',
              display: 'flex', alignItems: 'center', gap: '6px',
              transition: 'background 0.2s',
            }}
          >
            <Scissors size={13} /> 导出片段
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
}

// ── 子组件 ─────────────────────────────────────────────────────────────────

/** 工具按钮（底部操作栏） */
function AToolBtn({ onClick, children, color }) {
  const [hovered, setHovered] = useState(false);
  return (
    <button
      onClick={onClick}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={{
        padding: '5px 12px', borderRadius: '5px',
        border: `1px solid ${hovered ? color : 'var(--ed-border)'}`,
        background: hovered ? `${color}18` : 'transparent',
        color: hovered ? color : 'var(--ed-text2)',
        fontSize: '11px', cursor: 'pointer',
        transition: 'all 0.15s',
        whiteSpace: 'nowrap',
        display: 'flex', alignItems: 'center', gap: '4px',
      }}
    >{children}</button>
  );
}
